'use client';

import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { useBuilderStore } from '@/store/builderStore';
import Input from '@/app/components/ui/Input';
import Textarea from '@/app/components/ui/Textarea';
import Select from '@/app/components/ui/Select';
import Button from '@/app/components/ui/Button';
import { COUNTRIES } from '@/types/product';
import { User, Building2, Mail, Phone, Globe, Package, ArrowRight } from 'lucide-react';
import { useState } from 'react';

interface FormData {
  productName: string;
  description: string;
  customerName: string;
  companyName: string;
  email: string;
  phone: string;
  country: string;
}

const countryOptions = COUNTRIES.map((c) => ({ value: c, label: c }));

export default function ProductDescription() {
  const { customer, product, setCustomer, setProduct, setCurrentStep } = useBuilderStore();
  const [charCount, setCharCount] = useState(product.description.length);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      productName: product.productName,
      description: product.description,
      customerName: customer.customerName,
      companyName: customer.companyName,
      email: customer.email,
      phone: customer.phone,
      country: customer.country,
    },
  });

  const descriptionValue = watch('description');

  const onSubmit = (data: FormData) => {
    setProduct({ productName: data.productName, description: data.description });
    setCustomer({
      customerName: data.customerName,
      companyName: data.companyName,
      email: data.email,
      phone: data.phone,
      country: data.country,
    });
    setCurrentStep(2);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -24 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/25">
            <Package size={18} className="text-white" />
          </div>
          <div>
            <h2 className="text-lg font-black text-slate-800 tracking-tight">Describe Your Product</h2>
            <p className="text-sm text-slate-400">Tell us exactly what you&apos;re looking for</p>
          </div>
        </div>

        {/* Product Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-1 h-4 rounded-full bg-amber-500" />
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-widest">
              Product Details
            </h3>
          </div>

          <Input
            label="Product Name"
            placeholder="e.g., Luxury Brass Table Lamp"
            required
            leftIcon={<Package size={16} />}
            error={errors.productName?.message}
            {...register('productName', {
              required: 'Product name is required',
              minLength: { value: 3, message: 'At least 3 characters required' },
            })}
          />

          <div>
            <Textarea
              label="Product Description"
              placeholder='Example: "I need a luxury brass table lamp with antique finish and handmade engraving. The lamp should have a vintage colonial style with intricate floral patterns."'
              rows={5}
              required
              charCount={charCount}
              maxChars={500}
              error={errors.description?.message}
              {...register('description', {
                required: 'Description is required',
                minLength: { value: 20, message: 'At least 20 characters required' },
                maxLength: { value: 500, message: 'Max 500 characters' },
                onChange: (e) => setCharCount(e.target.value.length),
              })}
            />
          </div>
        </div>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-100" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Customer Information
            </span>
          </div>
        </div>

        {/* Customer Section */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Customer Name"
              placeholder="John Smith"
              required
              leftIcon={<User size={16} />}
              error={errors.customerName?.message}
              {...register('customerName', {
                required: 'Customer name is required',
              })}
            />
            <Input
              label="Company Name"
              placeholder="Acme Corp Ltd"
              leftIcon={<Building2 size={16} />}
              error={errors.companyName?.message}
              {...register('companyName')}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Email Address"
              type="email"
              placeholder="john@company.com"
              required
              leftIcon={<Mail size={16} />}
              error={errors.email?.message}
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Invalid email address',
                },
              })}
            />
            <Input
              label="Phone Number"
              type="tel"
              placeholder="+1 (555) 000-0000"
              leftIcon={<Phone size={16} />}
              error={errors.phone?.message}
              {...register('phone')}
            />
          </div>

          <Select
            label="Country"
            required
            placeholder="Select your country"
            options={countryOptions}
            error={errors.country?.message}
            {...register('country', { required: 'Country is required' })}
          />
        </div>

        {/* Tip */}
        <div className="flex gap-3 p-4 bg-amber-50 border border-amber-100 rounded-xl">
          <div className="text-amber-500 mt-0.5 shrink-0">💡</div>
          <p className="text-xs text-amber-700 leading-relaxed">
            <strong>Pro Tip:</strong> Be as specific as possible in your description. Include style,
            usage, special requirements, and any specific design elements you&apos;re looking for.
            This helps our AI generate more accurate product previews.
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end pt-2">
          <Button type="submit" size="lg" className="gap-2 min-w-40">
            Next Step
            <ArrowRight size={18} />
          </Button>
        </div>
      </form>
    </motion.div>
  );
}
